import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import {
  i18nTranslate,
  isSepaPaymentRecord,
  capitalizeFirstLetter,
} from 'src/utils'
import { FaCreditCard as CreditCardIcon } from 'react-icons/fa'
import { Button, Image, Modal } from 'react-bootstrap'
import { PaymentIcon, PaymentMarketForm } from 'src/views/components'
import EditUpdatePaymentModal from './EditUpdatePaymentModal'
import { customerContainer } from 'src/models'
import { dateFormatDeps } from 'src/deps'
import './styles.scss'

@observer
class PaymentCard extends React.Component {
  @observable isEditEnabled = false
  @observable shouldShowConfirmationView = false
  @observable showEditUpdateModal = false
  @observable IdofCardToBeEdited = ''
  @observable showEditSubscriptionModal = false

  toggleEditState = () => {
    this.isEditEnabled = !this.isEditEnabled
  }

  toggleConfirmationView = () => {
    this.shouldShowConfirmationView = !this.shouldShowConfirmationView
  }

  toggleEditUpdateValue = (e, id = 0) => {
    this.showEditUpdateModal = !this.showEditUpdateModal
    this.IdofCardToBeEdited = id
    //canceling the previous payment form
    const cancelButton = document.getElementById(
      'new-payment-cancel-btn-profile'
    )
    if (cancelButton) {
      cancelButton.click()
    }
  }
  toggleEditSubscriptionValue = (e, id = 0) => {
    this.showEditSubscriptionModal = !this.showEditSubscriptionModal
    this.IdofCardToBeEdited = id
  }
  handleDefaultPayment = () => {
    const postData = { isDefault: true }
    customerContainer.updateDefaultPayment({
      postData,
      id: this.props.cardData.id,
    })
  }

  handleRemovePayment = () => {
    this.toggleConfirmationView()
  }

  handleSavePayment = updatedDetail => {
    const { cardFirstName, expiryDate, setToDefaultPayment, ...addressParams } =
      updatedDetail
    const postData = {
      expiryDate,
      cardFirstName,
      isDefault: setToDefaultPayment,
      billingAddress: {
        email: customerContainer.profileResponse.email,
        type: 'billingAddress',
        ...addressParams,
      },
    }
    customerContainer.updatePayment({
      id: this.props.cardData.id,
      postData,
    })
    this.toggleEditState()
  }

  handleDeletePayment = () => {
    let cardDetails = {
      ...this.props.cardData,
      ...this.props.pageDetails,
    }

    customerContainer.deletePayment(cardDetails)
    this.toggleConfirmationView()
  }

  handleDeletePaymentMethod = cardData => {
    const cardNumber = cardData?.accountInfo?.cardNumber || ''
    const cardProvider = cardData?.accountInfo?.provider || ''
    const isSepaPayment = isSepaPaymentRecord(cardData)
    const sepaToken =
      cardData?.accountInfo?.token ||
      cardData?.accountInfo?.iban ||
      cardData?.accountInfo?.IBAN ||
      cardData?.properties?.iban ||
      ''
    const sepaEndingDigits = sepaToken ? sepaToken.slice(-4) : ''

    return (
      <div>
        <div className="delete-msg-wrapper">
          <span>
            {i18nTranslate('payment.deleteMessageStart', 'Delete')}&nbsp;
          </span>
          {isSepaPayment ? (
            <>
              <span>
                {i18nTranslate(
                  'payment.sepaDirectDebitEndingIn',
                  'Sepa Direct Debit ending in '
                )}
              </span>
              <span>{sepaEndingDigits}&nbsp;</span>
            </>
          ) : (
            <>
              <span>{capitalizeFirstLetter(cardProvider)} &nbsp;</span>
              <span>****{cardNumber}&nbsp;</span>
            </>
          )}
          <span>
            {i18nTranslate(
              'payment.deleteMessageEnd',
              'from your saved payments?'
            )}
          </span>
        </div>
        <div className="d-flex justify-content-between">
          <button
            className="btn btn-outline-primary cancel-btn"
            onKeyPress={this.toggleConfirmationView}
            onClick={this.toggleConfirmationView}>
            {i18nTranslate('payment.cancelDelete', 'Cancel')}
          </button>
          <button
            className="btn btn-primary confirm-btn"
            onKeyPress={() => {
              this.handleDeletePayment()
            }}
            onClick={() => {
              this.handleDeletePayment()
            }}>
            {i18nTranslate('payment.confirmDelete', 'Confirm')}
          </button>
        </div>
      </div>
    )
  }

  /**
   * @description if card is expired then update button will render else else button will render.
   * @date 9/28/2023 - 12:31:58 PM
   *
   * @param {*} cardData contains all card related details
   * @param {*} isCardValid flag to check card expired or not
   * @returns {*} edit/update and delete button in MyAccount-> payments page
   */
  renderButton = (cardData, isCardValid, isDeleteOnly = false) => {
    const cardNumber = cardData?.accountInfo?.cardNumber || ''
    const cardProvider = cardData?.accountInfo?.provider || ''
    const endingDigits = cardNumber
      ? cardNumber.substr(cardNumber.length - 4)
      : undefined

    return (
      <div className="button-wrapper">
        {!isDeleteOnly && (
          <span>
            <Button
              aria-label={`${i18nTranslate(
                'payment.editBtnText',
                'Edit button for'
              )} ${cardProvider} ${i18nTranslate(
                'payment.cardAndCardNumber',
                'card & card number'
              )} ${endingDigits}`}
              role="button"
              tabIndex={0}
              className="remove-button d-inline px-5"
              variant="outline-primary"
              onClick={e => this.toggleEditUpdateValue(e, cardData?.id)}
              data-testid={isCardValid ? 'qa-edit-button' : 'qa-update-button'}>
              <span aria-hidden="true">
                {isCardValid
                  ? i18nTranslate('edit', 'Edit')
                  : i18nTranslate('catalog.Update', 'Update', {
                      nameSpace: 'ssr-resource',
                    })}
              </span>
            </Button>
          </span>
        )}
        <span>
          <Button
            aria-label={`${i18nTranslate(
              'payment.removeBtnText',
              'Delete button for'
            )} ${cardProvider} ${i18nTranslate(
              'payment.cardAndCardNumber',
              'card & card number'
            )} ${endingDigits}`}
            role="button"
            tabIndex={0}
            className="remove-button d-inline px-5"
            variant="outline-primary"
            onClick={this.handleRemovePayment}
            data-testid="qa-delete-button">
            <span aria-hidden="true">
              {i18nTranslate('cart.delete', 'Delete')}
            </span>
          </Button>
        </span>
      </div>
    )
  }

  renderCardIcon = icon => {
    if (icon) {
      return (
        <Image
          src={icon}
          className="icon mr-2 mt-1 rounded"
          data-testid="qa-card-logo"
          alt={i18nTranslate('payment.cardIcon', 'Card logo')}
        />
      )
    } else {
      return (
        <CreditCardIcon
          className="icon mr-2 mt-1 rounded"
          aria-label={i18nTranslate('payment.cardIcon', 'Card logo')}
        />
      )
    }
  }

  renderCardDetail = cardData => {
    const cardNumber = cardData?.accountInfo?.cardNumber || ''
    const name = {
      firstName: cardData?.accountInfo?.name || '',
      lastName: cardData?.accountInfo?.lastName || '',
    }
    /**
     * @todo need to add further card type
     */
    const iconType = (cardData?.accountInfo?.provider || '').toLowerCase()
    const endingDigits = cardNumber
      ? cardNumber.substr(cardNumber.length - 4)
      : undefined
    const endingText = i18nTranslate('payment.cardEndingText', 'card ending in')
    const expiryText = i18nTranslate('payment.cardExpiresText', 'Expires')
    const expiredText = i18nTranslate(
      'payment.cardExpiredText',
      'Card is Expired'
    )
    const currentDateTimeStamp = Date.now()
    const { month, year } =
      dateFormatDeps.convertTimeStampToDate(currentDateTimeStamp)

    const getCardExpiredStatus = () => {
      return (
        year < cardData?.accountInfo?.expiryYear ||
        (year == cardData?.accountInfo?.expiryYear &&
          month <= cardData?.accountInfo?.expiryMonth)
      )
    }

    const isCardValid = getCardExpiredStatus()
    const expryTxt = isCardValid ? expiryText : expiredText
    const cardProvider = cardData?.accountInfo?.provider || ''

    return (
      <div className="payment-detail">
        <div className="card-detail d-flex pb-3">
          <PaymentIcon type={iconType} />
          <div className="account-info-wrapper">
            <span
              data-testid="qa-type"
              className="heading-3 mb-2">{` ${cardProvider} ${endingText} ${endingDigits}`}</span>
            <span
              data-testid="qa-expirydate"
              className={
                isCardValid ? 'paragraph-m' : 'paragraph-m text-danger'
              }>{`${expryTxt} ${
              cardData?.accountInfo?.expiryMonth
            }/${cardData?.accountInfo?.expiryYear?.substr(2)}`}</span>
            <span
              className="holder_name"
              data-testid="qa-payment-holder-name">{`${name?.firstName?.toUpperCase()} ${
              name?.firstName?.toUpperCase() !== name?.lastName?.toUpperCase()
                ? name?.lastName?.toUpperCase()
                : ''
            }`}</span>
            {/* {!isCardValid && cardData?.accountInfo?.isDefault && (
              <div
                className="subscription-card-link-btn"
                onClick={e => {
                  this.toggleEditSubscriptionValue(e, cardData?.id)
                }}
                cardId={cardData?.id}>
                {i18nTranslate('subscription.updateCard', 'Update Card')}
              </div>
            )} */}
          </div>
          {cardData?.accountInfo?.balance && (
            <div
              className="d-flex balance-wrapper"
              style={{ fontWeight: 'bold' }}>
              <span className="balance-text paragraph-m">
                {i18nTranslate('balance', 'Balance', {
                  nameSpace: 'ssr-resource',
                })}
              </span>
              <span className="balance-amount paragraph-m">
                {cardData?.accountInfo?.balance}
              </span>
            </div>
          )}
        </div>
        <div className="button-wrapper">
          {this.renderButton(cardData, isCardValid)}
        </div>
      </div>
    )
  }

  renderAlternatepaymentDetail = cardData => {
    const isSepaPayment = isSepaPaymentRecord(cardData)
    const sepaToken =
      cardData?.accountInfo?.token ||
      cardData?.accountInfo?.iban ||
      cardData?.accountInfo?.IBAN ||
      cardData?.properties?.iban ||
      ''
    const sepaEndingDigits = sepaToken ? sepaToken.slice(-4) : ''
    const buttonStyles = {
      justifyContent: this.shouldShowConfirmationView
        ? 'flex-end'
        : 'space-between',
    }
    return (
      <div>
        <div className="d-flex paymentType-block text-left">
          <PaymentIcon
            type={isSepaPayment ? 'sepa' : cardData?.type?.toLowerCase()}
          />
          <p className="paymentType-block-texts ml-2 text-left">
            {cardData?.type === 'ALIPAY' &&
              i18nTranslate('checkout.alipayText', 'AliPay')}
            {cardData?.type === 'PAYPAL' &&
              i18nTranslate('checkout.paypalText', 'PayPal')}
            {isSepaPayment && (
              <>
                {i18nTranslate(
                  'payment.sepaDirectDebitEndingIn',
                  'Sepa Direct Debit ending in'
                )}
                &nbsp;
                {sepaEndingDigits}
              </>
            )}
          </p>
        </div>
        <div className="button-wrapper" style={buttonStyles}>
          {this.renderButton(cardData, true, isSepaPayment)}
        </div>
      </div>
    )
  }

  render() {
    const { cardData, index } = this.props
    const isDefault = cardData?.accountInfo?.isDefault || false

    return (
      <div className="payment-card-wrapper" data-testid="qa-payment-container">
        {isDefault && (
          <span className="text-success ml-2">
            {i18nTranslate('payment.defaultTitle', 'Default Payment')}
          </span>
        )}
        <div className="payment-card-container p-3">
          {this.isEditEnabled ? (
            <PaymentMarketForm
              index={index}
              paymentData={cardData}
              isFromPayment={true}
              isFromEditPayment={true}
              includePaymetricCvv={false}
              onSubmitForm={this.handleSavePayment}
              onCancel={this.toggleEditState}
              billingAddressType="paymentsMethod"
            />
          ) : cardData?.type === 'CREDITCARD' ||
            cardData?.type === 'DEBITCARD' ? (
            this.renderCardDetail(cardData)
          ) : (
            this.renderAlternatepaymentDetail(cardData)
          )}
        </div>
        {/* {this.showEditSubscriptionModal  && (
          <EditSubscriptionPayment
            toggleEditSubscriptionValue={this.toggleEditSubscriptionValue}
            IDofCardToBeEdited={this.IdofCardToBeEdited}
            closeModalCallback={() => {
              this.showEditSubscriptionModal = false
            }}
            billingAddressType="paymentsMethod"
          />
        )} */}
        {/** my account payment cards edit/ update modal */}
        {this.showEditUpdateModal && (
          <EditUpdatePaymentModal
            show={this.showEditUpdateModal}
            closeButton={this.toggleEditUpdateValue}
            billingAddressType="paymentsMethod"
            IDofCardToBeEdited={this.IdofCardToBeEdited}
            isFromCheckout={false}
            cardDetails={cardData}
            closeModalCallback={() => {
              this.showEditUpdateModal = false
            }}
          />
        )}
        {/** my account payment cards delete modal */}
        {this.shouldShowConfirmationView && (
          <div>
            <Modal
              className={`visible modal-wrapper p-0`}
              show={this.shouldShowConfirmationView}
              onHide={() => (this.shouldShowConfirmationView = false)}
              centered
              animation={false}>
              <Modal.Header
                closeButton={this.toggleConfirmationView}
                className={'border-bottom-0'}>
                <Modal.Title className="title-Payment-Modal">
                  {i18nTranslate('checkout.deletePayment', 'Delete Payment')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {this.handleDeletePaymentMethod(cardData)}
              </Modal.Body>
            </Modal>
          </div>
        )}
      </div>
    )
  }
}

export { PaymentCard }
export default PaymentCard
